import React, { useEffect } from 'react';
import { Label, TextInput, Textarea, Card } from 'flowbite-react';
import { MultiLanguageFieldProps } from '../types';
import { LanguageDropdown } from '../../LanguageSelectionDropdown/LanguageDropdown';
import { useAppSelector } from '../../../../redux/hooks/useAppSelector';
import { selectIsDarkMode } from '../../../../redux/selectors/displaySettingsSelectors';

/**
 * MultiLanguageField - Generic multi-language input field component
 *
 * **Component Type:** Stateless (Controlled)
 *
 * **Purpose:**
 * Handles single/multiple language-aware input fields with built-in validation,
 * language selection, and support for custom renderers (e.g., MDEditor).
 *
 * **State Management:**
 * - No internal state - fully controlled by parent via props
 * - Language selection managed by parent (useMultiLanguageInput hook)
 * - Value updates flow through onChange callback
 *
 * **Usage:**
 * ```tsx
 * const { selectedLanguage, setSelectedLanguage } = useMultiLanguageInput();
 *
 * <MultiLanguageField
 *   config={{
 *     id: 'title',
 *     label: 'common.title',
 *     type: 'text',
 *     required: true,
 *     validation: { maxLength: 100 }
 *   }}
 *   value={data.title || []}
 *   onChange={(value) => onUpdate('title', value)}
 *   selectedLanguage={selectedLanguage}
 *   onLanguageChange={setSelectedLanguage}
 *   t={t}
 * />
 * ```
 *
 * **Supported Types:**
 * - 'text' - Single line text input
 * - 'textarea' - Multi-line text input
 * - 'markdown' - Markdown editor (requires customRenderer)
 * - 'custom' - Any custom input via customRenderer prop
 *
 * @param props - Component props including config, value, onChange, selectedLanguage
 * @returns React component
 */
export const MultiLanguageField: React.FC<MultiLanguageFieldProps> = ({
  config,
  value,
  onChange,
  onValidate,
  selectedLanguage,
  showLanguageDropdown = true,
  onLanguageChange,
  isDarkMode: providedIsDarkMode,
  t,
  customRenderer,
  wrapInCard = false,
}) => {
  const reduxIsDarkMode = useAppSelector(selectIsDarkMode);
  const isDarkMode = providedIsDarkMode ?? reduxIsDarkMode;

  const {
    id,
    label,
    type,
    placeholder,
    required = false,
    rows = 3,
    validation,
    className = '',
    disabled = false,
    autoFocus = false,
    languageSelectorLabel,
  } = config;

  // Get current value for selected language
  const getCurrentValue = (): string => {
    return (
      value.find((item) => item.language === selectedLanguage.languageCode)
        ?.value || ''
    );
  };

  // Handle value change
  const handleChange = (newValue: string) => {
    const existingEntry = value.find(
      (item) => item.language === selectedLanguage.languageCode
    );

    const updatedData = existingEntry
      ? value.map((item) =>
          item.language === selectedLanguage.languageCode
            ? { ...item, value: newValue }
            : item
        )
      : [
          ...value,
          { language: selectedLanguage.languageCode, value: newValue },
        ];

    // Filter out empty values
    const sanitizedData = updatedData.filter((item) => item.value !== '');

    onChange(sanitizedData);
  };

  // Validate field when value changes
  useEffect(() => {
    if (validation && onValidate) {
      const currentValue = getCurrentValue();

      // Check max length
      if (validation.maxLength && currentValue.length > validation.maxLength) {
        onValidate(
          false,
          `Maximum length is ${validation.maxLength} characters`
        );
        return;
      }

      // Check min length
      if (
        validation.minLength &&
        currentValue.length > 0 &&
        currentValue.length < validation.minLength
      ) {
        onValidate(
          false,
          `Minimum length is ${validation.minLength} characters`
        );
        return;
      }

      // Check pattern
      if (
        validation.pattern &&
        currentValue &&
        !validation.pattern.test(currentValue)
      ) {
        onValidate(false, validation.customMessage || 'Invalid format');
        return;
      }

      // Check custom validation
      if (
        validation.custom &&
        currentValue &&
        !validation.custom(currentValue)
      ) {
        onValidate(false, validation.customMessage || 'Validation failed');
        return;
      }

      // Check if required field has at least one value
      if (required) {
        const hasValue = value.some((item) => item.value.trim() !== '');
        onValidate(hasValue, '');
      } else {
        onValidate(true, '');
      }
    }
  }, [value, selectedLanguage.languageCode, validation, required, onValidate]);

  // Build translated placeholder
  const getPlaceholder = (): string => {
    if (!placeholder) return '';
    const translatedPlaceholder = t(placeholder);
    const languageName = t(`languages.${selectedLanguage.languageCode}`);
    return `${translatedPlaceholder}${languageName}`;
  };

  // Render the appropriate input based on type
  const renderInput = () => {
    const currentValue = getCurrentValue();

    if (customRenderer) {
      return customRenderer({
        id,
        value: currentValue,
        onChange: handleChange,
        placeholder: getPlaceholder(),
        languageCode: selectedLanguage.languageCode,
        disabled,
        isDarkMode,
        className,
      });
    }

    switch (type) {
      case 'text':
        return (
          <TextInput
            id={id}
            type="text"
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={getPlaceholder()}
            disabled={disabled}
            autoFocus={autoFocus}
            className={className}
          />
        );

      case 'textarea':
        return (
          <Textarea
            id={id}
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={getPlaceholder()}
            rows={rows}
            disabled={disabled}
            className={`mt-1 ${className}`}
          />
        );

      case 'markdown':
      case 'custom':
        // These should use customRenderer
        return null;

      default:
        return null;
    }
  };

  const content = (
    <>
      {showLanguageDropdown && onLanguageChange && (
        <div className="mb-4">
          <Label
            value={t(
              languageSelectorLabel ||
                'contentContext.selectTitleAndDescLanguage'
            )}
            className="mb-2"
          />
          <LanguageDropdown
            selectedLanguage={selectedLanguage}
            onSelectLanguage={onLanguageChange}
            isDarkMode={isDarkMode}
          />
        </div>
      )}

      <div className="mb-4">
        <Label htmlFor={id}>
          {`${t(label)} (${selectedLanguage.label})`}
          {required && <span className="ms-1 text-red-500">*</span>}
        </Label>
        {renderInput()}
      </div>
    </>
  );

  if (wrapInCard) {
    return <Card>{content}</Card>;
  }

  return <>{content}</>;
};
